<template>
  <div class="smx-auto z-10 bg-slate-50">
    <nav
      class="mx-auto max-w-3xl font-sans text-gray-600 antialiased lg:max-w-6xl"
    >
      <div class="px-8 py-4 md:py-4 lg:py-8">
        <div class="mx-auto flex h-16 justify-between lg:max-w-6xl">
          <!-- Left -->
          <div class="flex">
            <!-- Mobile Menu Toggler -->
            <div class="-ml-2 mr-2 flex items-center lg:hidden">
              <button
                type="button"
                class="inline-flex items-center justify-center rounded-md p-2 text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                aria-controls="mobile-menu"
                aria-expanded="false"
                @click.prevent="toggleMobileNav"
              >
                <HamburgerMenu />
              </button>
            </div>

            <!-- Logo -->
            <div class="flex flex-shrink-0 items-center">
              <NuxtLink
                class="text-xl font-bold text-gray-900"
                to="/"
              >
                <VeridisLogo />
              </NuxtLink>
            </div>

            <!-- Links -->
            <div
              class="hidden md:hidden lg:ml-6 lg:flex lg:items-center lg:space-x-4"
            >
              <div class="relative">
                <NuxtLink
                  to="/"
                  class="block px-4 py-2 text-base font-medium text-gray-600"
                  aria-current="page"
                >Home
                </NuxtLink>
                <div
                  class="active absolute -bottom-0.5 left-0 right-0 mx-auto h-1.5 w-1.5 rounded-full bg-green-700 lg:block"
                />
              </div>
              <NuxtLink
                to="#About"
                class="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-800"
              >
                Product
              </NuxtLink>
              <NuxtLink
                to="#FAQ"
                class="text-md block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-800"
              >
                FAQ</NuxtLink>
              <NuxtLink
                to="/contact-us"
                class="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-800"
              >Let's Talk</NuxtLink>
            </div>
          </div>

          <!-- Right -->
          <div class="flex items-center">
            <!-- Authenticated -->
            <div
              v-if="$store.auth.isLoggedIn"
              class="hidden md:ml-4 md:flex md:flex-shrink-0 md:items-center"
            >
              <div class="relative ml-3">
                <div>
                  <button
                    type="button"
                    class="flex rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-200"
                    aria-expanded="false"
                    aria-haspopup="true"
                    @click.prevent="toggleDropdown"
                  >
                    <avatar
                      class="h-10 w-10 rounded-full"
                      variant="beam"
                      :colors="colors"
                      :name="$store.auth.authenticatedUser.first_name"
                    />
                  </button>
                </div>
                <div
                  class="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  :class="{ hidden: !opened }"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu-button"
                  tabindex="-1"
                >
                  <NuxtLink
                    v-if="
                      $store.auth.authenticatedUser?.is_zuno_engineer === true
                    "
                    to="/admin/home"
                    class="block px-4 py-2 text-sm text-gray-700"
                    role="menuitem"
                    tabindex="-1"
                  >
                    Dashboard</NuxtLink>
                  <NuxtLink
                    v-if="
                      $store.auth.authenticatedUser.is_zuno_engineer === false
                    "
                    to="/dashboard/home"
                    class="block px-4 py-2 text-sm text-gray-700"
                    role="menuitem"
                    tabindex="-1"
                  >
                    Dashboard</NuxtLink>
                  <a
                    href="#"
                    class="block px-4 py-2 text-sm text-gray-700"
                    role="menuitem"
                    tabindex="-1"
                    @click="$keycloak.logoutUser()"
                  >Logout</a>
                </div>
              </div>
            </div>

            <div
              v-else
              class="flex"
            >
              <NuxtLink
                to="/login/"
                class="mr-6 block px-4 py-4 text-base font-medium text-gray-600 hover:text-gray-800"
                aria-current="page"
              >Log in</NuxtLink>
              <div class="flex-shrink-0">
                <NuxtLink
                  to="/register"
                  class="block select-none rounded-md border-2 border-green-800/50 bg-green-700 px-5 py-3 text-sm font-medium text-white shadow hover:bg-green-800 hover:shadow-md active:bg-green-700 lg:px-8 lg:py-3 lg:text-base"
                >
                  <span>Register</span>
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        id="mobile-menu"
        class="bg-white px-8 lg:hidden"
        :class="{ hidden: !navOpened }"
      >
        <div class="space-y-1 px-2 pb-3 pt-2 sm:px-3">
          <NuxtLink
            to="/"
            class="block px-3 py-2 text-base font-medium text-gray-600"
            aria-current="page"
          >
            Home
          </NuxtLink>
          <NuxtLink
            to="#About"
            class="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-800"
          >Product
          </NuxtLink>
          <NuxtLink
            to="/"
            class="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-800"
          >
            Pricing
          </NuxtLink>
          <NuxtLink
            to="#FAQ"
            class="text-md block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-800"
          >
            FAQ
          </NuxtLink>
          <NuxtLink
            to="/contact-us"
            class="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-800"
          >
            Let's Talk
          </NuxtLink>
        </div>
        <div
          v-if="$store.auth.isLoggedIn"
          class="border-t border-gray-700 pb-3 pt-4"
        >
          <button
            type="button"
            class="flex rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-200"
            aria-expanded="false"
            aria-haspopup="true"
            @click.prevent="toggleDropdown"
          >
            <avatar
              class="h-10 w-10 rounded-full"
              variant="beam"
              :colors="colors"
              :name="$store.auth.authenticatedUser.first_name"
            />
          </button>
          <div class="mt-3 space-y-1 px-2 sm:px-3">
            <NuxtLink
              v-if="$store.auth.authenticatedUser"
              :to="
                $store.auth.authenticatedUser?.is_zuno_engineer
                  ? '/admin/home'
                  : '/dashboard/home'
              "
              class="block px-4 py-2 text-sm text-gray-700"
              role="menuitem"
              tabindex="-1"
            >Dashboard
            </NuxtLink>
            <a
              href="#"
              class="block px-4 py-2 text-sm text-gray-700"
              role="menuitem"
              tabindex="-1"
              @click="$keycloak.logoutUser()"
            >Logout</a>
          </div>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import Avatar from 'vue-boring-avatars'

export default {
  name: 'HomeNavbar',

  components: {
    Avatar,
  },

  data() {
    return {
      opened: false,
      navOpened: false,
      colors: ['#FFAD08', '#EDD75A', '#73B06F', '#0C8F8F', '#405059'],
    }
  },

  methods: {
    toggleDropdown() {
      this.opened = !this.opened
    },
    toggleMobileNav() {
      this.navOpened = !this.navOpened
    },
  },
}
</script>
